import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useSnackbar from '../../../hooks/useSnackbar';
import { ITool, IToolBag } from '../../../utils/entities/interface';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';
import { ModifyToolBag } from '../../../services/tool';

interface FormData {
  _id?: string;
  toolname: string;
  description: string;
  quantity: number;
}

interface ToolAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  toolBag?: IToolBag | null;
  tool?: ITool | null;
}
const TiTModal = ({
  open,
  onClose,
  onSaved,
  tool,
  toolBag,
}: ToolAddEditProps) => {
  const { showSnackbar, SnackbarComponent, resetSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<FormData>({
    toolname: '',
    description: '',
    quantity: 1,
  });

  useEffect(() => {
    if (tool) {
      setFormData({
        _id: tool._id,
        toolname: tool.name || '',
        description: tool.description || '',
        quantity: tool.quantity || 1, // Default to 1 if quantity is not provided
      });
    } else {
      setFormData({ toolname: '', description: '', quantity: 1 });
    }
  }, [tool]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      if (!toolBag) return;
      if (!toolBag._id) return;
      if (!tool || !tool._id) return;
      const status = await ModifyToolBag({
        toolBagId: toolBag._id,
        toolId: tool._id,
        action: 'add',
        name: formData.toolname,
        description: formData.description,
        quantity: formData.quantity,
      });

      if (status === 200 || status === 201) {
        showSnackbar(
          `Tool ${formData._id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setTimeout(() => {
          onClose();
          resetModal();
          if (onSaved) onSaved();
        }, 1500);
      } else {
        showSnackbar(
          `Error: Failed to ${formData._id ? 'update' : 'create'} tool`,
          'error',
        );
      }
    } catch (error: any) {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  const resetModal = () => {
    setFormData({ toolname: '', description: '', quantity: 1 });
    onClose();
    resetSnackbar();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'hidden', // Ensures the background doesn't leak
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #7B1FA2', // Add border here
          borderRadius: 10,
          overflow: 'hidden', // Prevent border from affecting layout
        }}
      >
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#181818',
            padding: '16px',
          }}
        >
          {'Modify Toolbag'}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
          <Grid container spacing={3}>
            {' '}
            <Grid item xs={6}>
              <Typography gutterBottom>Tool Name</Typography>
              <TextField
                name="toolname"
                value={formData.toolname}
                onChange={handleChange}
                fullWidth
                required
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Tool Quantity</Typography>
              <TextField
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                fullWidth
                type="number"
                required
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom> Description</Typography>
              <TextField
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                required
                multiline
                minRows={4}
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                sx={{
                  fontStyle: 'italic',
                  color: '#aaa',
                  mb: 1,
                }}
              >
                {tool
                  ? `Drop "${tool.name}" into ${toolBag?.name}'s kit`
                  : `Add a new tool to ${toolBag?.name}'s kit`}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <SecondaryButton title="Cancel" onClick={onClose} />
            <PrimaryButton title="Save" onClick={(e) => handleSubmit(e)} />
          </DialogActions>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TiTModal;
